<template>
  <q-card :class="$q.screen.xs ? 'q-pa-sm' : 'q-pa-md'">
    <div class="row no-wrap items-center justify-between text-h6 text-center">
      <div class="q-pr-sm">{{ pond.name }}</div>
      <div class="q-pb-sm col-auto">
        <q-btn
          unelevated
          label="Fish In / Out"
          color="primary"
          :padding="$q.screen.xs ? '2px 6px' : '2px 8px'"
          size="md"
          @click="$emit('goToFishInOut', pond)"
        />
      </div>
    </div>
    <q-separator />

    <div class="col row justify-between q-py-xs">
      <div>Head</div>
      <div>{{ PondInfo.head.toLocaleString() }}</div>
    </div>
    <q-separator />

    <div class="col row justify-between q-py-xs">
      <div>Estimated Weight</div>
      <div>{{ PondInfo.weight.toLocaleString() }}</div>
    </div>
    <q-separator />

    <div class="col row justify-between q-py-xs">
      <div>Fed Since Last Harvest</div>
      <div v-if="feedingDataLoading">Loading...</div>
      <div v-else>{{ totalFedSinceLastHarvest.toLocaleString() }}</div>
    </div>
    <q-separator />

    <div
      v-for="(group, index) of ActiveGroups"
      :key="`${group.stocking_id}-${index}`"
      class="col-12 q-pb-sm q-pl-md"
    >
      <div class="text-subtitle2 q-py-xs">
        {{ group.name }}
      </div>

      <div class="q-pl-md">
        <div class="row justify-between">
          <div>Size</div>
          <div>{{ group.size.toLocaleString() }}</div>
        </div>
        <q-separator />
      </div>

      <div class="q-pl-md">
        <div class="row justify-between">
          <div>Head</div>
          <div>{{ group.head.toLocaleString() }}</div>
        </div>
        <q-separator />
      </div>

      <div class="q-pl-md">
        <div class="row justify-between">
          <div>Estimated Weight</div>
          <div>{{ group.weight.toLocaleString() }}</div>
        </div>
        <q-separator />
      </div>
    </div>
    
    <div class="row justify-center q-pt-sm">
      <div 
        class="text-primary cursor-pointer"
        style="text-decoration: underline"
        @click="showJournalDialog = true"
      >
        View Journal Entries
      </div>
    </div>

    <!-- Journal Entries Dialog -->
    <div v-if="showJournalDialog" class="modal-backdrop" @click.self="showJournalDialog = false">
      <q-card class="journal-modal-card">
        <div class="row justify-between items-center q-pa-md">
          <div class="text-h6 text-primary">Journal Entries: {{ pond.name }}</div>
          <q-btn flat round icon="close" color="primary" @click="showJournalDialog = false" />
        </div>
        <q-separator />
        
        <q-card-section class="scroll" style="max-height: 70vh">
          <div v-if="pondJournals.length === 0" class="text-center text-subtitle1 q-pa-md">
            No journal entries found for this pond.
          </div>
          
          <div v-for="journal of pondJournals" :key="journal.id" class="q-mb-md">
            <q-card flat bordered class="q-pa-md">
              <div class="row justify-between">
                <div class="text-h6 text-primary q-pr-md">
                  {{ journal.subject || '---' }}
                </div>
                <div class="row items-center">
                  <q-icon
                    v-if="journal.calendar_display"
                    name="event"
                    color="black"
                    class="q-mr-xs"
                  />
                  <div>
                    {{ new Date(journal.date * 1000).toLocaleDateString() }}
                  </div>
                </div>
              </div>
              
              <div v-if="journal.equipment_id" class="text-weight-medium text-accent">
                {{ getEquipmentName(journal.equipment_id) }}
              </div>
              
              <div class="q-pt-sm q-pb-sm">
                <span v-if="journal.keyword.length > 0">
                  {{ journal.keyword }} -
                </span>
                {{ journal.journal_entry }}
              </div>
            </q-card>
          </div>
        </q-card-section>
      </q-card>
    </div>
  </q-card>
</template>

<script>
import store from '@/store';
import { decoder, numberWithCommas } from '@/lib/helpers';
import { date } from 'quasar';

export default {
  name: 'Pond-Overview',
  props: {
    pond: {
      type: Object,
      required: true
    },
    feedingData: {
      type: Array,
      default: () => []
    },
    feedingDataLoading: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      numberWithCommas,
      showJournalDialog: false
    };
  },

  methods: {
    getEquipmentName(equipmentId) {
      if (!equipmentId) {
        return '';
      }
      
      const equipment = this.EquipmentList.find((x) => x.value === equipmentId);
      return equipment ? equipment.label : '';
    },
    
    getNumGroups(pond) {
      return numGroups;
    }
  },
  computed: {
    ActiveGroups() {
      if (!this.pond.growth || !this.pond.growth.FishGroups) {
        return [];
      }
      return this.pond.growth.FishGroups.filter(
        (group) => group.status === 'active'
      );
    },
    
    EquipmentList() {
      const equipmentList = [];
      
      for (const equipment of store.state.equipmentList) {
        equipmentList.push({
          label: equipment.name,
          value: equipment.id
        });
      }
      
      return equipmentList;
    },
    
    lastHarvestDate() {
      // Explicitly access pond.id to ensure reactivity when pond changes
      if (!this.pond || !this.pond.id) {
        return null;
      }
      
      const history = this.pond?.growth?.ManageHistory ?? [];
      
      // Filter for harvest entries and find the most recent one
      const harvests = history.filter((entry) => entry.transfer_type === 'Harvest');
      
      if (harvests.length === 0) {
        return null; // No harvests found
      }
      
      // Sort by date (most recent first) and get the first one
      harvests.sort((a, b) => b.date - a.date);

      return harvests[0].date;
    },
    
    PondInfo() {
      const info = {
        numGroups: 0,
        head: 0,
        weight: 0
      };

      if (this.pond.growth && this.pond.growth.FishGroups) {
        for (const group of this.pond.growth.FishGroups) {
          if (group.status !== 'active') {
            continue;
          }

          info.numGroups++;
          info.head += group.head;
          info.weight += group.weight;
        }
      }

      return info;
    },
    
    pondJournals() {
      // Get all journal entries from store
      const allJournals = this.$store.state.dailyJournals || [];
      
      // Filter journal entries to only include this specific pond
      return allJournals.filter((journal) => 
        journal.pond_ids.includes(this.pond.id)
      ).sort((a, b) => b.date - a.date); // Sort by date, newest first
    },
    
    totalFedSinceLastHarvest() {
      if (this.feedingDataLoading) {
        return 0; // Return 0 while loading (though template won't show this)
      }
      
      const lastHarvest = this.lastHarvestDate;
      
      // If no harvest found, calculate total fed from beginning of time
      const startDate = lastHarvest || 0;
      
      // Use the feeding data prop passed from parent
      const total = this.feedingData
        .filter((feeding) => feeding.date > startDate)
        .reduce((total, feeding) => total + (feeding.pounds || 0), 0);
      
      return total;
    }
  }
};
</script>

<style>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.07);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.journal-modal-card {
  width: 700px;
  max-width: 90vw;
  max-height: 90vh;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}
</style>
